import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ContentTemplate,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TABLE_INITIAL_STATE,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';

import { StatusCodes } from 'http-status-codes';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { usePostSearchCandidateHistories } from '../../../../api-refactored/exam-configuration/usePostSearchCandidateHistories';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import DraftDateTimeStamp from '../../../../components/draft-date-time-stamp/DraftDateTimeStamp';
import { existInArray } from '../../../../utils/ArrayUtils';
import UploadCandidateHistoriesModal from './UploadCandidateHistoriesModal';

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    paddingBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      paddingBottom: theme.spacing(1),
    },
  },
  addCandidateButton: {
    minWidth: '200px',
  },
  searchContainer: {
    paddingBottom: theme.spacing(3),
  },
  removeButton: {
    color: theme.color.state.stateError,
  },
  addedByText: {
    color: theme.color.text.textTertiary,
  },
  filterSelect: {
    paddingBottom: theme.spacing(3),
  },
}));

interface Input {
  selectedExamTypeUuid: string;
  selectedExamTypeDisplay: string;
  selectedFeatureTypeDisplay: string;
  setNoAccess: Dispatch<SetStateAction<boolean>>;
}

const useGetTableColumns = (): MUIDataTableColumnDef[] => {
  const { t } = useTranslation();

  return [
    {
      name: 'nric',
      label: t('exam->>ExamConfiguration.Nric'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
      },
    },
    {
      name: 'citizenship',
      label: t('exam->>ExamConfiguration.Citizenship'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
      },
    },
  ];
};

const resetDataTable = (tableDispatch: React.Dispatch<TableActionTypes>) => {
  tableDispatch({
    type: 'UPDATE_PAGE',
    payload: 0,
  });
};

const { FORBIDDEN } = StatusCodes;

const CandidateHistory = ({
  selectedExamTypeUuid,
  selectedExamTypeDisplay,
  selectedFeatureTypeDisplay,
  setNoAccess,
}: Input) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const { snackbarHandler } = useSnackbarHandler();
  const [numberOfRecords, setNumberOfRecords] = useState<number>(0);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [uploadedBy, setUploadedBy] = useState<string | null>();
  const [updatedAt, setUpdatedAt] = useState<string | null>();

  const authorities = useGetUserAuthorities();
  const hasCreateAccess = existInArray(
    authorities.data,
    AUTHORITY_NAMES.CP_ADMIN_CANDIDATE_HISTORY_CREATE
  );

  const { state: tableState, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'nricFin',
    order: 'asc',
    size: 10,
    page: 0,
    search: '',
  });

  const callSearchCandidateHistoriesMutate = () => {
    candidateHistoryMutate({
      uuid: selectedExamTypeUuid,
      body: { q: tableState.search },
      apiParams: {
        page: tableState.page,
        pageSize: tableState.size,
        order: tableState.order,
        sort: tableState.sortColumn,
      },
    });
  };

  const onCandidateSearch = (candidateFilter: string) => {
    tableDispatch({
      type: 'UPDATE_SEARCH',
      payload: candidateFilter.trim(),
    });
  };

  const {
    isLoading: isCandidateHistoryLoading,
    mutate: candidateHistoryMutate,
  } = usePostSearchCandidateHistories({
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.candidateHistoryListingModel.content,
            count: data.candidateHistoryListingModel.totalElements,
          },
        } as TableActionTypes);
      }
      setUploadedBy(data.name);
      setUpdatedAt(data.lastUpdatedTime);
    },
    onError: (err) => {
      if (err.response?.status === FORBIDDEN) {
        setNoAccess(true);
      }
    },
  });

  useEffect(() => {
    callSearchCandidateHistoriesMutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidateHistoryMutate,
    tableState.search,
    tableState.page,
    tableState.size,
    tableState.order,
    tableState.sortColumn,
  ]);

  return (
    <>
      <ContentTemplate type="full">
        <Box className={classes.headerContainer}>
          <SectionHeader title={`${selectedExamTypeDisplay} ${selectedFeatureTypeDisplay}`} />
          {hasCreateAccess && (
            <Box>
              <Button
                id="button-candidateBlacklist-cancel"
                variant="text"
                color="primary"
                startIcon={
                  <Icon name={IconName.export} size="S" color={theme.color.icon.iconSelected} />
                }
                onClick={() => setShowUploadModal(true)}
                className={classes.addCandidateButton}
              >
                {t('exam->>ExamConfiguration.UploadCandidateList')}
              </Button>
            </Box>
          )}
        </Box>

        <SearchBar
          className={classes.searchContainer}
          name="searchInputCandidateHistory"
          ariaLabel="searchInputCandidateHistory"
          placeholder={t('exam->>ExamConfiguration.SearchCandidateNric')}
          onSearch={(str) => {
            onCandidateSearch(str);
          }}
        />

        <Box>
          <MUIDataTable
            key="candidate"
            isLoading={isCandidateHistoryLoading}
            columns={useGetTableColumns()}
            data={tableState.data}
            dispatch={tableDispatch}
            state={tableState}
            options={{
              ...tOptions,
              filter: false,
              sort: false,
              pagination: true,
            }}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>

        <DraftDateTimeStamp
          lastUpdatedInfo={{
            lastUpdatedTime: updatedAt,
            name: uploadedBy,
          }}
        />
      </ContentTemplate>

      <UploadCandidateHistoriesModal
        selectedExamTypeUuid={selectedExamTypeUuid}
        modalState={showUploadModal}
        onDialogClose={() => setShowUploadModal(false)}
        resetDataTable={() => {
          resetDataTable(tableDispatch);
        }}
        callSearchCandidateHistoriesMutate={callSearchCandidateHistoriesMutate}
        displayUploadSuccessSnackbar={(noOfRecords: number) => {
          snackbarHandler?.setGlobalSnackbar({
            targetSeverity: 'success',
            targetData: t('exam->>ExamConfiguration.UpdatedCandidateHistorySuccessfully', {
              numberOfRecords,
            }),
          });
          setNumberOfRecords(noOfRecords);
        }}
      />
    </>
  );
};

export default CandidateHistory;
